import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type { FaxItem } from '../../services/faxItems';
import type { RouteFaxRequest } from '../../services/faxRouting';
import FaxDetailsFields from './FaxDetailsFields';
import OrderAnswerLine from './OrderAnswerLine';
import PatientOrDraftPicker, {
  type PickerSelection,
} from './PatientOrDraftPicker';
import RouteDestinationFields from './RouteDestinationFields';
import { patientLabel } from './patientOrDraftLabels';
import { NONE, type FaxItemEditor } from './useFaxItemEditor';
import { useRouteDestination } from './useRouteDestination';
import { LEAVE_GUARD_REPLACE } from '../../hooks/useLeaveGuard';

export interface FaxRoutePanelProps {
  item: FaxItem;
  editor: FaxItemEditor;
  /** False while the loaded row is not yet the one in the URL. */
  itemMatches: boolean;
  /** Anything in flight that ends by leaving or reloading this fax. */
  busy: boolean;
  /** A filing is in flight, for the File to chart spinner. */
  filing: boolean;
  /** The agency has Roger EMR, so there is a chart to file to. */
  canFile: boolean;
  /** The user may start an intake. */
  canIntake: boolean;
  /** Back to the filtered list, for a fax that has left it. */
  listUrl: string;
  onSave: () => void;
  onFile: (
    patientId: string,
    request: Omit<RouteFaxRequest, 'patient_id' | 'fax_type_id'>
  ) => void;
  onCreateIntake: () => void;
  onRemove: () => void;
  onDecideSignature: (signed: boolean) => void;
  onToggleArchive: () => void;
  onError: (message: string) => void;
}

/**
 * What the fax is and where it goes: file it to an existing patient's chart,
 * or start an intake from it. One obvious next action per fax.
 */
export default function FaxRoutePanel({
  item,
  editor,
  itemMatches,
  busy,
  filing,
  canFile,
  canIntake,
  listUrl,
  onSave,
  onFile,
  onCreateIntake,
  onRemove,
  onDecideSignature,
  onToggleArchive,
  onError,
}: FaxRoutePanelProps) {
  const [tab, setTab] = useState<'existing' | 'intake'>('existing');
  // Seeded once from the row. A draft-linked fax seeds nothing: the picker
  // here offers patients only.
  const [patient, setPatient] = useState<PickerSelection | null>(
    item.patient_id
      ? {
          kind: 'patient',
          id: item.patient_id,
          label: item.patient_label ?? 'Patient',
        }
      : null
  );
  // The row's patient the picker last took. A returned-order match can fill
  // one in later: an untouched picker follows it, a user's choice stays.
  const [rowPatientId, setRowPatientId] = useState(item.patient_id ?? null);
  if ((item.patient_id ?? null) !== rowPatientId) {
    setRowPatientId(item.patient_id ?? null);
    if ((patient?.id ?? null) === rowPatientId) {
      setPatient(
        item.patient_id
          ? {
              kind: 'patient',
              id: item.patient_id,
              label: item.patient_label ?? 'Patient',
            }
          : null
      );
    }
  }
  const typeId = editor.typeId === NONE ? null : editor.typeId;
  // A filed or merged fax is not routed again, so its tasks are not looked up.
  const routable = canFile && !item.chart_filed_at && !item.filed;
  const route = useRouteDestination(
    routable ? (patient?.id ?? null) : null,
    typeId,
    onError
  );

  const chartFiled = !!item.chart_filed_at;
  const merged = item.filed;
  const archived = !!item.archived_at;
  const working = busy || editor.saving;
  const readOnly = chartFiled || merged;
  // A filed fax keeps its pages and destination; its name, type and tags stay
  // editable. A merged arrival is replaced by its document, so nothing is.
  const detailsLocked = merged;

  const fileEnabled =
    canFile &&
    route.canFile &&
    !!patient?.id &&
    item.status === 'ready' &&
    !archived &&
    !merged &&
    !chartFiled &&
    itemMatches &&
    !working;

  const intakeEnabled =
    item.status === 'ready' &&
    !archived &&
    !readOnly &&
    itemMatches &&
    !working;

  const typeField = (
    <div className='space-y-1.5'>
      <Label htmlFor='fax-type'>Type</Label>
      <Select
        value={editor.typeId}
        onValueChange={editor.setTypeId}
        disabled={working || !itemMatches}
      >
        <SelectTrigger id='fax-type' aria-label='Type'>
          <SelectValue placeholder='Select' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>None</SelectItem>
          {editor.typeOptions.map(type => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className='flex flex-col gap-5'>
      <FaxDetailsFields
        editor={editor}
        disabled={detailsLocked || !itemMatches || working}
        stale={!itemMatches}
        onError={onError}
      />

      <OrderAnswerLine
        item={item}
        deciding={editor.deciding}
        archiving={editor.archiving}
        saving={working}
        disabled={!itemMatches}
        decideSignature={onDecideSignature}
        toggleArchive={onToggleArchive}
      />

      {editor.dirty && !detailsLocked && (
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={working || !itemMatches}
            onClick={onSave}
          >
            {editor.saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save changes
          </Button>
          <span className='text-xs text-muted-foreground'>Unsaved changes</span>
        </div>
      )}

      {chartFiled ? (
        <div className='flex flex-col gap-4'>
          {typeField}
          <p className='text-xs text-muted-foreground'>
            Changes here update the inbox only. The copy on the chart keeps its
            name.
          </p>
          <p className='text-sm'>
            {item.patient_id && item.chart_task_id ? (
              <>
                Filed to{' '}
                <Link
                  className='font-medium text-primary underline underline-offset-2'
                  to={`/patients/${item.patient_id}/visits/${item.chart_task_id}`}
                >
                  {item.chart_task_label ?? 'its task'}
                </Link>
              </>
            ) : (
              'Filed to the chart'
            )}
          </p>
        </div>
      ) : merged ? (
        <p className='text-sm'>
          This fax was made into a document.{' '}
          <Link
            className='font-medium text-primary underline underline-offset-2'
            to={listUrl}
            replace
            {...LEAVE_GUARD_REPLACE}
          >
            Back to the list
          </Link>
        </p>
      ) : (
        <Tabs
          value={tab}
          onValueChange={value => setTab(value as 'existing' | 'intake')}
        >
          <TabsList className='w-full'>
            <TabsTrigger
              value='existing'
              className='min-w-0 flex-1 whitespace-normal text-xs leading-tight sm:text-sm'
            >
              Existing patient
            </TabsTrigger>
            {canIntake && (
              <TabsTrigger
                value='intake'
                className='min-w-0 flex-1 whitespace-normal text-xs leading-tight sm:text-sm'
              >
                Create intake
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value='existing' className='mt-4 flex flex-col gap-4'>
            {canFile ? (
              <PatientOrDraftPicker
                patientsOnly
                selected={patient}
                onChoosePatient={chosen =>
                  setPatient({
                    kind: 'patient',
                    id: chosen.id,
                    label: patientLabel(chosen),
                  })
                }
                onClear={() => setPatient(null)}
                disabled={working || !itemMatches}
                onError={onError}
              />
            ) : (
              <p className='text-sm text-muted-foreground'>
                Filing to a chart needs Roger EMR.
              </p>
            )}

            {typeField}

            {canFile && patient?.id && (
              <RouteDestinationFields
                destination={route}
                disabled={working || !itemMatches}
              />
            )}

            {archived && (
              <p className='text-sm text-muted-foreground'>
                Move this fax back to the inbox first.
              </p>
            )}
            {!archived && item.status !== 'ready' && (
              <p className='text-sm text-muted-foreground'>
                This fax has not finished downloading.
              </p>
            )}

            <div className='flex flex-wrap justify-end gap-2'>
              <Button
                variant='outline'
                disabled={working || !itemMatches}
                onClick={onRemove}
              >
                Remove from inbox
              </Button>
              {canFile && (
                <Button
                  disabled={!fileEnabled}
                  onClick={() => {
                    if (patient?.id) onFile(patient.id, route.request());
                  }}
                >
                  {filing && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                  File to chart
                </Button>
              )}
            </div>
          </TabsContent>

          {canIntake && (
            <TabsContent value='intake' className='mt-4 flex flex-col gap-4'>
              <p className='text-sm text-muted-foreground'>
                Start an intake with this fax attached. You can add other PDFs.
              </p>
              {archived && (
                <p className='text-sm text-muted-foreground'>
                  Move this fax back to the inbox first.
                </p>
              )}
              <div className='flex flex-wrap justify-end gap-2'>
                <Button
                  variant='outline'
                  disabled={working || !itemMatches}
                  onClick={onRemove}
                >
                  Remove from inbox
                </Button>
                <Button disabled={!intakeEnabled} onClick={onCreateIntake}>
                  Create intake
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}

      {readOnly && (
        <div className='flex flex-wrap justify-end gap-2'>
          <Button
            variant='outline'
            disabled={working || !itemMatches}
            onClick={onRemove}
          >
            Remove from inbox
          </Button>
        </div>
      )}
    </div>
  );
}
